import type * as A from '@/api';
import DashboardWidget from '@/services/dashboardWidget/abstractDashboardWidget';
import DashboardWidgetFactory from './dashboardWidget/dashboardWidgetFactory';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { v4 as uuid } from 'uuid';

const ns = 'app.services.dashboard';

type DashboardConstructorInput = A.GetDashboardQuery['getDashboard'];

/**
 * Dashboard query cache mode.
 * Phase 1: 'off' (no cache) | 'on' (read-through cache).
 * Phase 2 (managed): 'auto' | 'custom' — beem-agent proactively refreshes.
 */
export type DashboardCacheMode = 'off' | 'on' | 'auto' | 'custom';

/** The two managed modes in which beem-agent proactively refreshes. */
export const MANAGED_CACHE_MODES: ReadonlySet<DashboardCacheMode> = new Set(['auto', 'custom']);

/** True for 'auto' | 'custom'. Central helper used by the crossing detection. */
export function isManagedCacheMode(mode: DashboardCacheMode): boolean {
  return MANAGED_CACHE_MODES.has(mode);
}

/**
 * PLACEHOLDER shape of a single custom filter combination (open question #2).
 * Kept intentionally loose; the full picker UX (open question #5) will replace it.
 * The frontend treats combinations as opaque and forwards them to beem-agent as-is.
 */
export type FilterCombination = {
  /** Stable id so the placeholder picker can key/remove entries. */
  id: string;
  /** Opaque per-viz filter selections. Refined when the picker UX lands. */
  filters: Record<string, unknown>;
};

/** Default staleness tolerance for cached results: 7 days, expressed in minutes. */
export const DEFAULT_STALENESS_TOLERANCE_MINUTES = 7 * 24 * 60; // 10080

export type DashboardSettings = {
  columns: number;
  gap: number;
  rowHeight: number;
  /** Query cache mode for this dashboard's visualization widgets (Phase 1). */
  cacheMode: DashboardCacheMode;
  /** How old a cached result can be before initial load skips it, in minutes. */
  stalenessToleranceMinutes: number;
  /**
   * Custom-mode filter combinations (Phase 2). A single per-dashboard SHARED set
   * (Req 4.2) stored inside the settings JSON blob. Optional: absent on dashboards
   * saved before this feature and on non-custom dashboards.
   */
  cacheCustomCombinations?: FilterCombination[];
  /**
   * Auto-mode recency window in DAYS (Phase 2, Req 1.5/1.6). The number of days
   * since a cache entry's last hit that 'auto' mode treats as "recently used".
   * A frontend setting the frontend forwards; the worker consuming it is out of
   * scope. Mirrors stalenessToleranceMinutes. Defaults to 7.
   */
  cacheAutoRecencyDays: number;
};

/** Default auto-mode recency window in days (Req 1.6). */
export const DEFAULT_CACHE_AUTO_RECENCY_DAYS = 7;

export const DEFAULT_SETTINGS: DashboardSettings = {
  columns: 14,
  gap: 12,
  rowHeight: 64,
  cacheMode: 'off',
  stalenessToleranceMinutes: DEFAULT_STALENESS_TOLERANCE_MINUTES,
  cacheAutoRecencyDays: DEFAULT_CACHE_AUTO_RECENCY_DAYS, // Req 1.6 — default recency window
  // cacheCustomCombinations intentionally omitted from defaults; normalized on construction.
};

/** A single visualization entry in the cache-refresh config payload (Req 6). */
export type CacheRefreshConfigViz = { vizId: string; datasetId: string };

/** Payload the frontend sends to beem-agent on a Cache_Mode_Crossing (Req 6, 7). */
export type CacheRefreshConfigPayload = {
  dashboardId: string;
  mode: DashboardCacheMode;
  vizzes: CacheRefreshConfigViz[];
  /** Present only when mode === 'custom' (Req 6.3/6.4). */
  filterCombinations?: FilterCombination[];
  /** Present only when mode === 'auto' (Req 6.6/6.7). Recency window in days. */
  autoRecencyDays?: number;
};

export default class Dashboard {
  readonly id: string;

  readonly name: string;

  readonly workspaceId: string;

  readonly settings: DashboardSettings;

  readonly createdAt: Date;

  readonly updatedAt: Date;

  private constructor(input: DashboardConstructorInput) {
    this.id = input.id;
    this.name = input.name;
    this.workspaceId = input.workspaceId;
    const parsed: DashboardSettings = input.settings
      ? { ...DEFAULT_SETTINGS, ...JSON.parse(input.settings) }
      : DEFAULT_SETTINGS;
    // Req 1.3 — treat missing combinations as an empty set.
    // Req 1.7 — cacheAutoRecencyDays already defaulted to 7 by the DEFAULT_SETTINGS merge.
    this.settings = { ...parsed, cacheCustomCombinations: parsed.cacheCustomCombinations ?? [] };
    this.createdAt = new Date(input.createdAt);
    this.updatedAt = new Date(input.updatedAt);

    this.saveWidgets = this.saveWidgets.bind(this);
  }

  /**
   * Returns a shallow copy of this Dashboard with the given settings applied.
   * Used to optimistically update state after a settings save.
   */
  withSettings(settings: DashboardSettings): Dashboard {
    const clone = Object.create(Dashboard.prototype) as Dashboard;
    Object.assign(clone, this, { settings });
    clone.saveWidgets = clone.saveWidgets.bind(clone);
    return clone;
  }

  buildInput(w: DashboardWidget) {
    return {
      id: w.id,
      type: w.type,
      dashboardId: this.id,
      configurations: JSON.stringify(w.getConfigurations()),
      position: JSON.stringify(w.position),
      workspaceId: this.workspaceId,
    };
  }

  async saveWidgets(next: DashboardWidget[], prev: DashboardWidget[] = []): Promise<void> {
    const nextWidgetsById = new Map(next.map((w) => [w.id, w]));
    const prevWigdetsById = new Map(prev.map((w) => [w.id, w]));

    const toCreate = next.filter((w) => !prevWigdetsById.has(w.id));
    const toUpdate = next.filter((w) => prevWigdetsById.has(w.id));
    const toDelete = prev.filter((w) => !nextWidgetsById.has(w.id));

    const calls: Promise<unknown>[] = [
      ...toCreate.map((w) => graphql('createDashboardWidget', { input: this.buildInput(w) })),
      ...toUpdate.map((w) => graphql('updateDashboardWidget', { input: this.buildInput(w) })),
      ...toDelete.map((w) => graphql('deleteDashboardWidget', { input: { id: w.id } })),
    ];

    await Promise.all(calls);
  }

  static async getDashboardById(id: string): Promise<Dashboard> {
    logger.info(id, { label: `${ns}.getDashboardById.id` });
    const data = await graphql('getDashboard', { id });
    return new Dashboard(data);
  }

  static async getDashboardsByWorkspace(workspaceId: string): Promise<Dashboard[]> {
    logger.info(workspaceId, { label: `${ns}.getDashboardsByWorkspace.workspaceId` });
    const data = await graphql('listDashboardsByWorkspace', { workspaceId });
    return data.items.map((el) => new Dashboard(el));
  }

  static async create(input: A.CreateDashboardInput): Promise<string> {
    logger.info({ label: `${ns}.create.input`, message: input });
    const data = await graphql('createDashboard', { input });
    return data.id;
  }

  static async update(input: A.UpdateDashboardInput): Promise<string> {
    logger.info({ label: `${ns}.update.input`, message: input });
    const data = await graphql('updateDashboard', { input: input });
    return data.id;
  }

  static async updateSettings(id: string, settings: DashboardSettings): Promise<void> {
    logger.info({ label: `${ns}.updateSettings`, message: { id, settings } });
    await graphql('updateDashboard', {
      input: { id, settings: JSON.stringify(settings) },
    });
  }

  static async setCacheRefreshConfig(payload: CacheRefreshConfigPayload): Promise<void> {
    logger.info({ label: `${ns}.setCacheRefreshConfig`, message: payload });
    // Command name / input shape confirmed with beem-agent (open question #1, RESOLVED).
    // orgId/wsSeq are injected by the graphql() helper for beemAgent* commands.
    await graphql('beemAgentSetDashboardCacheConfig', {
      query: {
        dashboardId: payload.dashboardId,
        mode: payload.mode,
        vizzes: payload.vizzes,
        // Serialized to a JSON string only for custom mode (Req 6.3/6.4); the
        // generated field is a flat string, consistent with other beem-agent JSON fields.
        filterCombinations:
          payload.mode === 'custom' ? JSON.stringify(payload.filterCombinations ?? []) : undefined,
        // Req 6.6/6.7 — recency window forwarded only for auto mode.
        autoRecencyDays: payload.mode === 'auto' ? payload.autoRecencyDays : undefined,
      },
    });
  }

  /**
   * Notify beem-agent that a visualization has been permanently deleted.
   * Called by Visualization.delete() after deleteDatasetVisualization succeeds.
   * beem-agent deletes the entire {datasetId}/{vizId}.json S3 config file.
   * orgId/wsSeq injected by the graphql() helper.
   */
  static async notifyVizDeleted({
    datasetId,
    vizId,
  }: {
    datasetId: string;
    vizId: string;
  }): Promise<void> {
    logger.info({ label: `${ns}.notifyVizDeleted`, message: { datasetId, vizId } });
    await graphql('beemAgentNotifyVizDeleted', { query: { datasetId, vizId } });
  }

  /**
   * Notify beem-agent that a visualization widget has been removed from a specific dashboard.
   * Called by the invokeSaveWidgets effect after Dashboard.saveWidgets succeeds, once per
   * Removed_Widget. beem-agent removes that dashboard's entry from the viz's S3 config file.
   * orgId/wsSeq injected by the graphql() helper.
   */
  static async notifyVizRemovedFromDashboard({
    datasetId,
    vizId,
    dashboardId,
  }: {
    datasetId: string;
    vizId: string;
    dashboardId: string;
  }): Promise<void> {
    logger.info({
      label: `${ns}.notifyVizRemovedFromDashboard`,
      message: { datasetId, vizId, dashboardId },
    });
    await graphql('beemAgentNotifyVizRemovedFromDashboard', {
      query: { datasetId, vizId, dashboardId },
    });
  }

  static async delete(id: string): Promise<void> {
    logger.info({ label: `${ns}.delete.id`, message: id });
    const widgets = await DashboardWidgetFactory.getDashboardWidgetsByDashboard(id);
    await Promise.all(
      widgets.map((widget) => {
        return graphql('deleteDashboardWidget', { input: { id: widget.id } });
      }),
    );
    await graphql('deleteDashboard', { input: { id } });
  }

  static async duplicate(id: string, newName: string): Promise<string> {
    logger.info({ label: `${ns}.duplicate`, message: { id, newName } });
    const source = await Dashboard.getDashboardById(id);
    const newDashboardId = await Dashboard.create({
      name: newName,
      workspaceId: source.workspaceId,
      settings: JSON.stringify(source.settings),
    });
    const widgets = await DashboardWidgetFactory.getDashboardWidgetsByDashboard(id);
    await Promise.all(
      widgets.map((widget) => {
        const input: A.CreateDashboardWidgetInput = {
          id: uuid(),
          dashboardId: newDashboardId,
          type: widget.type,
          configurations: JSON.stringify(widget.getConfigurations()),
          position: JSON.stringify(widget.position),
          workspaceId: source.workspaceId,
        };
        return graphql('createDashboardWidget', { input });
      }),
    );
    return newDashboardId;
  }
}
